import i18next from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import es from "./locales/es.json";

/**
 * Translation, for a game that is played out loud in a room.
 *
 * # The resources are bundled, never fetched
 *
 * `i18next-http-backend` is the usual way to load locales, and it is the wrong
 * one here. `/display` ships as a **packaged television app** (ADR 0005, phase
 * 11d) where the page is a file inside the bundle: `location.origin` is the
 * string `"null"` and there is no host to ask for `/locales/es.json`. A fetched
 * locale would work in every browser and leave a television rendering raw
 * message keys.
 *
 * Two smaller reasons point the same way. The service worker **caches nothing**
 * on purpose (`services/installable.ts`), so a fetched locale is a real network
 * round trip at boot — on a LAN, at a party, on somebody's phone. And a
 * language that arrives one tick after first paint means the front door renders
 * in English and then flips, which reads as a bug.
 *
 * The cost is that both languages are in every bundle. **Measured after the
 * 2026-08-14 sweep: 43 kB of JSON against a 700 kB bundle** — 6%, and far less
 * after gzip, since this is repetitive prose. That is still comfortably worth
 * paying for a locale that is present at first paint.
 *
 * The number to watch is the *share*, not the sweep: two languages cost this
 * much, and a third or fourth costs it again while only one of them is ever
 * read. When that share stops being defensible the answer is `import()` per
 * language at the router boundary — **not** a network backend, which the
 * packaged television still cannot use for the reason above.
 *
 * # Detection, in ten lines rather than a dependency
 *
 * `i18next-browser-languagedetector` handles a dozen sources — cookies, query
 * strings, `<html lang>`, subdomains. This app has exactly two: what somebody
 * chose last time, and what their browser says. That is small enough to own,
 * and the dependency-minimalism rule asks for the justification either way.
 */

/** The languages that exist today. Adding one is this array plus a JSON file. */
export const LANGUAGES = ["en", "es"] as const;

/** A language this build actually ships. */
export type Language = (typeof LANGUAGES)[number];

/** What each language calls itself — never translated, by convention. */
export const LANGUAGE_NAMES: Record<Language, string> = {
  en: "English",
  es: "Español",
};

/** Where the chosen language is remembered. */
const STORAGE_KEY = "sonolume.language";

/** Whether a string is a language this build ships. */
function isLanguage(value: string | null | undefined): value is Language {
  return typeof value === "string" && (LANGUAGES as readonly string[]).includes(value);
}

/**
 * The language to start in.
 *
 * A stored choice wins over the browser, because somebody who switched to
 * Spanish on a phone whose OS is English meant it. `navigator.language` is
 * matched on the **primary subtag** — `es-419`, `es-MX` and `es-ES` are all
 * Spanish here, and a game that refused `es-MX` because it only knows `es` would
 * be absurd.
 */
export function detectLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(stored)) return stored;
  } catch {
    // Private browsing, or a page with no storage access. Not an error worth
    // reporting — the browser's own preference is a perfectly good answer.
  }

  for (const tag of navigator.languages ?? [navigator.language]) {
    const primary = tag.split("-")[0]?.toLowerCase();
    if (isLanguage(primary)) return primary;
  }
  return "en";
}

/** Switch language and remember it. */
export async function setLanguage(language: Language): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Remembering is a nicety; changing it now is the point.
  }
  document.documentElement.lang = language;
  await i18next.changeLanguage(language);
}

/**
 * Start i18next.
 *
 * Called once from each entry point — `main.tsx` and `main.tv.tsx` — rather than
 * as an import side effect, so a test can initialise it deliberately and the
 * packaged television build can differ if it ever needs to.
 */
export async function initI18n(language: Language = detectLanguage()): Promise<void> {
  document.documentElement.lang = language;

  await i18next.use(initReactI18next).init({
    lng: language,
    // English is the source language: every key exists there by construction,
    // because `es.json` is type-checked against it.
    fallbackLng: "en",
    resources: {
      en: { translation: en },
      es: { translation: es },
    },
    interpolation: {
      // React escapes everything it renders already, and double-escaping turns
      // an apostrophe in "Somebody's turn" into an entity on screen.
      escapeValue: false,
    },
    // A missing key should be loud in development and invisible to a player.
    // Returning the key renders `home.join.title` on screen, which is worse than
    // English for somebody mid-party.
    returnEmptyString: false,
  });
}

export { i18next };
